<template>
	<div v-loading="loadingWorkflow" id="previewCanvas">
		<cytoscape
			:key="reload"
			id="cy"
			:preConfig="preConfig"
			:afterCreated="afterCreated"
			ref="cy"
			:style="{ width: '100%;', height: '100%', opacity: loadingWorkflow ? '0.4' : '1' }"
			:config="config"
		>
			<cy-element @click="nodeClicked" v-for="def in nodes" :key="`${def.data.id}`" :definition="def" />
		</cytoscape>
	</div>
</template>

<script>
import cyCanvas from "cytoscape-canvas";
import input from "../assets/node.svg";
import filledInput from "../assets/input.svg";
let globalThis;
export default {
	props: { workflowObject: Object },
	computed: {
		workflow() {
			return this.workflowObject;
		},
	},
	mounted() {
		if (this.workflow) {
			this.loadWorkflow();
		}
	},
	watch: {
		workflow: function () {
			this.loadWorkflow();
		},
	},
	methods: {
		loadWorkflow() {			
			if (!this.workflow) {
				return;
			}
			this.loadingWorkflow = true;
			this.nodes = [];
			this.reload = !this.reload;
			setTimeout(() => {
				this.setConnectorType();
				this.interpretWorkflow();
			}, 300);
			setTimeout(() => {
				this.loadingWorkflow = false;
			}, 500);
		},
		setConnectorType() {
			this.cy.edges().forEach((edge) => {
				edge.css({
					"curve-style": this.workflow.settings.canvasConnectorStyle || "unbundled-bezier",
				});
				if (edge.target().data().specialType) {
					this.updateEdgeDistances(edge);
				}
			});
		},
		addContextualInputsToNode({ id }, position) {
			let inputs = this.cy.nodes(`#${id}`).data().inputs;
			if (!inputs?.length) return;
			inputs = inputs.filter((each) => {
				return typeof each === "object";
			});
			const contextualNodeIds = [];
			inputs.forEach((input, index) => {
				const contextualId = id + "_" + input.type;
				contextualNodeIds.push(contextualId);
				this.cy.add({
					group: "nodes",
					data: {
						specialType: "contextualNodeInput",
						node: input,
						id: contextualId,
						parentNodeId: id,
					},
					renderedPosition: {
						x: this.getRelativeContextualPosition(inputs.length, index, position),
						y: position.y + 45 * this.cy.zoom(),
					},
				});
			});
		},
		getRelativeContextualPosition(totalInputs, inputPositionIsList, position) {
			const parentWidth = 50;
			const offsetXFactor = 0.45 * this.cy.zoom();
			const totalWidth = (totalInputs - 1) * offsetXFactor * parentWidth;
			const startX = position.x - totalWidth / 2;
			const currentX = startX + inputPositionIsList * offsetXFactor * parentWidth;
			return currentX;
		},
		splitByFirstCharacterOccurrence(str, char) {
			const parts = str.split(char);
			return [parts[0], parts.slice(1).join(char)];
		},
		interpretWorkflow() {
			const targetNodeExists = (targetId, nodeName, nodeOutputs) => {
				const target = this.workflow.workflowNodes.find((eachNode) => {
					const type = this.splitByFirstCharacterOccurrence(targetId, "_")[1];
					const typeFilter = eachNode.inputs?.find((each) => {
						return each.type === type;
					})?.filter?.nodes;
					let filterPassed = true;
					if (typeFilter && !typeFilter.includes(nodeName)) {
						filterPassed = false;
					}
					return (
						eachNode.id === targetId ||
						(eachNode.id === targetId.split("_")[0] &&
							eachNode.inputs
								?.map((each) => {
									return each.type;
								})
								.includes(type) &&
							filterPassed &&
							nodeOutputs?.length) //We check that outputs exist, otherwise we don't allow connection to contextual input, as node is not associated without outputs
					);
				});
				return target ? true : false;
			};
			this.cy.zoom(this.workflow.settings.canvasZoomLevel || 1);
			const workflowNodes = this.workflow.workflowNodes;
			const edges = [];
			workflowNodes.forEach((node) => {
				node.outgoingConnections.forEach((edgeId) => {
					if (targetNodeExists(edgeId, node.name, node.outputs)) {
						edges.push({
							group: "edges",
							data: {
								source: node.id,
								target: edgeId,
							},
						});
					}
				});
			});
			this.cy.add(
				workflowNodes.map((node) => {
					return {
						group: "nodes",
						data: {
							node: node,
							id: node.id,
							inputs: node.inputs || node.nodeDescription.inputs || [],
							outputs: node.outputs || node.nodeDescription.outputs || [],
						},
						renderedPosition: node.nodeSettings.canvasPosition,
					};
				})
			);
			this.cy.nodes().forEach((each) => {
				const node = each.data().node;
				this.addContextualInputsToNode(node, node.nodeSettings.canvasPosition);
			});
			this.cy.add(edges);
			this.cy.edges().forEach(this.updateEdgeDistances);
			this.cy.nodes().forEach((each) => {
				if (each.data().specialType && each.data().node.maxConnections && each.connectedEdges().length) {
					each.style({
						"background-image": "url(" + filledInput + ")",
					});
				}
			});
			setTimeout(() => {
				this.cy.fit(10);
			}, 0);
		},
		updateEdgeDistances(edge) {
			if (this.workflow.settings.canvasConnectorStyle === "taxi" || edge.target().data().specialType) {
				edge.css({
					"curve-style": "taxi",
				});
			}
			let sourceEndpointX = 0;
			let sourceEndpointY = 0;
			let targetEndpointX = 0;
			let targetEndpointY = 0;
			const { x: sx, y: sy } = edge.source().position();
			const { x: tx, y: ty } = edge.target().position();
			const dx = tx - sx;
			const dy = ty - sy;

			// Decide if the connection is more horizontal or vertical
			if (Math.abs(dx) >= Math.abs(dy)) {
				// It's more horizontal
				if (dx >= 0) {
					sourceEndpointX = 50;
					sourceEndpointY = 0;
					targetEndpointX = -50;
					targetEndpointY = 0;
				} else {
					sourceEndpointX = -50;
					sourceEndpointY = 0;
					targetEndpointX = 50;
					targetEndpointY = 0;
				}
			} else {
				// It's more vertical
				if (dy >= 0) {
					sourceEndpointX = 0;
					sourceEndpointY = 50;
					targetEndpointX = 0;
					targetEndpointY = -50;
				} else {
					sourceEndpointX = 0;
					sourceEndpointY = -50;
					targetEndpointX = 0;
					targetEndpointY = 50;
				}
			}

			edge.data("endpoints-source", [sourceEndpointX + "%", sourceEndpointY + "%"]);
			edge.data("endpoints-target", [targetEndpointX + "%", targetEndpointY + "%"]);

			const fraction = 0.3; // tweak to taste
			let offset;

			if (Math.abs(dx) >= Math.abs(dy)) {
				// Primarily horizontal; use vertical difference as base
				offset = fraction * dy;
				// If target is to the left, invert the offset
				if (dx < 0) {
					offset = -offset;
				}
			} else {
				// Primarily vertical; use horizontal difference as base
				offset = fraction * dx;
				// If target is below, invert the offset
				if (dy > 0) {
					offset = -offset;
				}
			}

			edge.data("distances", [-offset, offset]); //P1, P2
			edge.data("weights", [0.25, 0.75]); //P0, P3
		},
		saveWorkflowAsImg(callback) {
			const canvas = document.createElement("canvas");
			canvas.id = "tempLayer";
			const ctx = canvas.getContext("2d");
			const mainLayer = this.cy.png();
			const overlayLayer = this.overlayLayer.toDataURL("image/png");
			const imageObj1 = new Image();
			const imageObj2 = new Image();
			imageObj1.src = mainLayer;
			imageObj2.src = overlayLayer;
			imageObj1.onload = function () {
				canvas.width = imageObj1.width;
				canvas.height = imageObj1.height;
				ctx.drawImage(imageObj1, 0, 0, imageObj1.width, imageObj1.height);
				imageObj2.onload = function () {
					ctx.drawImage(imageObj2, 0, 0, imageObj2.width, imageObj2.height);
					const base64Img = canvas.toDataURL("image/png");
					callback(base64Img);
				};
			};
		},
		nodeClicked() {
			return;
		},
		preConfig(cytoscape) {
			if (typeof cytoscape("core", "cyCanvas") !== "function") {
				cyCanvas(cytoscape);
			}
		},
		async afterCreated(cy) {
			this.cy = await cy;
			globalThis = this;
			cy.autolock(true);
			cy.maxZoom(10);
			cy.minZoom(0.3);
			cy.zoom({
				level: 2,
			});
			//Additional Canvas
			const overlayLayer = cy.cyCanvas({
				zIndex: 0,
			});
			this.overlayLayer = overlayLayer.getCanvas();
			const overlayCanvas = overlayLayer.getCanvas();
			const ctxOverlay = overlayCanvas.getContext("2d");
			//Render event listener
			cy.on("render cyCanvas.resize", () => {
				overlayLayer.resetTransform(ctxOverlay);
				overlayLayer.clear(ctxOverlay);
				overlayLayer.setTransform(ctxOverlay);
				ctxOverlay.save();
				cy.nodes().forEach((node) => {
					if (node.data().node) {
						const pos = node.position();
						ctxOverlay.fillStyle = "#A2A2B5";
						ctxOverlay.font = "6px Helvetica";
						let text = node.data().node.description || node.data().node?.nodeDescription?.description;
						ctxOverlay.fillText(text || "", pos.x + 31, pos.y - 7);
						if (!node.data().executionIndex) {
							ctxOverlay.fillStyle = "#210F3C";
							ctxOverlay.font = "9px Helvetica";
							if (node.data().node.type === "trigger" || node.data().node.type === "webhook") {
								ctxOverlay.fillText("🆃", pos.x - 21, pos.y - 12);
							}
							if (node.data().node.type === "action" && !node.data().node.outputs?.length) {
								ctxOverlay.fillText("🅰", pos.x - 21, pos.y - 12);
							}
						}
						if (node.data().node.type === "action" && !node.data().node.outputs?.length) {
							const mode = node.data().node.nodeSettings.mode || this.workflow.settings.workflowMode;
							ctxOverlay.fillStyle = "#210F3C";
							ctxOverlay.font = "10px Helvetica";
							ctxOverlay.fillText("⚙", pos.x + 31, pos.y + 14);
							ctxOverlay.font = "6px Helvetica";
							ctxOverlay.fillText(mode, pos.x + 39, pos.y + 13);
						}
					}
				});
				ctxOverlay.restore();
			});
		},
	},
	data() {
		return {
			reload: false,
			loadingWorkflow: true,
			overlayLayer: null,
			nodes: [],
			config: {
				cy: null,
				style: [
					{
						selector: "node",
						style: {
							width: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "8px";
								}
								return "50px";
							},
							height: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "8px";
								}
								return "50px";
							},
							color: function () {
								return "#293953";
							},
							"border-width": 0.8,
							"border-color": function () {
								return "#A2A2B5";
							},
							shape: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "ellipse";
								}
								return "round-rectangle";
							},
							"background-color": function () {
								return "white";
							},
							"background-fit": "none",
							label: function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return ele.data().node?.displayName?.split(" ")[0];
								}
								return ele.data().node?.nodeDescription?.displayName || " ";
							},
							"text-margin-y": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "-4px";
								}
								return "10px";
							},
							"text-margin-x": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "0px";
								}
								return "6px";
							},
							"text-halign": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "center";
								}
								return "right";
							},
							"font-size": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "4px";
								}
								return "8px";
							},
							"font-weight": "bold",
							"font-family": "Helvetica",
							"background-image": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return input;
								}
								const mime = ele.data().node?.iconMimeType === "png" ? "png" : "svg+xml";
								return ele.data().node
									? `url(data:image/${mime};base64,${ele.data().node.icon})`
									: "url(data:image/svg+xml;base64";
							},
							"background-height": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "5px";
								}
								return "35px";
							},
							"background-width": function (ele) {
								if (ele.data()?.specialType === "contextualNodeInput") {
									return "5px";
								}
								return "35px";
							},
							"bounds-expansion": "20px,",
							"background-clip": "none",
						},
					},
					{
						selector: "edge",
						style: {
							"curve-style": "unbundled-bezier",
							"control-point-distances": (edge) => edge.data("distances") || [0, 0],
							"control-point-weights": (edge) => edge.data("weights") || [0.25, 0.75],
							"taxi-turn": function () {
								return "90%";
							},
							"taxi-direction": function () {
								return "horizontal";
							},
							width: 1,
							"line-color": "#A2A2B5",
							"target-arrow-color": "#A2A2B5",
							"target-arrow-shape": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "none";
								}
								return "triangle";
							},
							"line-style": function () {
								return "dashed";
							},
							"source-endpoint": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "0% 0%";
								}
								if (globalThis.workflow.settings.canvasConnectorStyle === "taxi") {
									return "0% 0%";
								}
								return ele.data("endpoints-source") || "0% 0%";
							},
							"target-endpoint": function (ele) {
								const targetNode = globalThis.cy.nodes(`#${ele.data().target}`);
								if (targetNode.data().specialType) {
									return "0% 0%";
								}
								if (globalThis.workflow.settings.canvasConnectorStyle === "taxi") {
									return "outside-to-node";
								}
								return ele.data("endpoints-target") || "0% 0%";
							},
							"text-valign": "center", //Center the text vertically
							"text-rotation": "autorotate",
							color: "white",
							"text-background-padding": "1px",
							"font-size": "4px",
							"text-background-color": "#293953",
							"text-background-opacity": "1",
							"text-background-shape": "round-rectangle",
							"text-border-opacity": "1",
							"text-border-width": "6px",
							"text-border-color": "#293953",
						},
					},
				],
			},
		};
	},
};
</script>

<style scoped>
#previewCanvas {
	box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
	border: 2px solid black;
	border-radius: 4px;
	cursor: grab;
	position: relative;
	overflow: hidden;
	width: 100%;
	height: 300px;
	background-color: transparent;
	position: relative;
	z-index: 0;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='7' height='7' viewBox='0 0 1 16'%3E%3Cpath fill='%237e7c83' fill-opacity='1' d='M1 3h1v1H1V3zm2-2h1v1H3V1z'%3E%3C/path%3E%3C/svg%3E");
}

#previewCanvas /deep/ #cytoscape-div {
	height: 300px;
	min-height: 300px !important;
}

canvas {
	left: 0px;
}
</style>
